/**
 * Cryptographic helpers built exclusively on WebCrypto so the same code runs
 * in Cloudflare Workers, browsers and Node 20+.
 *
 * Passwords use PBKDF2-SHA256. Argon2id would be preferable but is not
 * available in the Workers runtime without WASM; the iteration count is set
 * high and is stored in the encoded hash so it can be raised later without a
 * migration (documented in docs/SECURITY_THREAT_MODEL.md).
 */

const enc = new TextEncoder();

export const PBKDF2_ITERATIONS = 100_000;

function toBase64(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin);
}

function fromBase64(s: string): Uint8Array<ArrayBuffer> {
  const bin = atob(s);
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/**
 * `atob` throws on any character outside the base64 alphabet. Every caller here
 * decodes ATTACKER-CONTROLLED text (a token from a request header, a stored
 * hash), so a throw would surface as a 500 instead of the typed rejection the
 * signature says the function returns. Never let that exception escape.
 */
function tryFromBase64(s: string): Uint8Array<ArrayBuffer> | null {
  try {
    return fromBase64(s);
  } catch {
    return null;
  }
}

export function toHex(bytes: ArrayBuffer | Uint8Array): string {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let out = '';
  for (const b of view) out += b.toString(16).padStart(2, '0');
  return out;
}

/** SHA-256 hex digest. Used for token lookup columns and content hashing. */
export async function sha256Hex(input: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', enc.encode(input));
  return toHex(digest);
}

/**
 * Keyed hash for PII lookup columns (phone_hash, email_hash).
 * The key is a per-tenant secret so a leaked table cannot be rainbow-tabled.
 */
export async function hmacSha256Hex(key: string, input: string): Promise<string> {
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    enc.encode(key),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const sig = await crypto.subtle.sign('HMAC', cryptoKey, enc.encode(input));
  return toHex(sig);
}

/** `pbkdf2-sha256$<iterations>$<saltB64>$<hashB64>` */
export async function hashPassword(password: string, iterations = PBKDF2_ITERATIONS): Promise<string> {
  const salt = crypto.getRandomValues(new Uint8Array(new ArrayBuffer(16)));
  const bits = await derive(password, salt, iterations);
  return `pbkdf2-sha256$${iterations}$${toBase64(salt)}$${toBase64(new Uint8Array(bits))}`;
}

export async function verifyPassword(password: string, encoded: string): Promise<boolean> {
  const parts = encoded.split('$');
  if (parts.length !== 4 || parts[0] !== 'pbkdf2-sha256') return false;
  const iterations = Number(parts[1]);
  if (!Number.isInteger(iterations) || iterations < 1000) return false;
  const salt = tryFromBase64(parts[2]!);
  const expected = tryFromBase64(parts[3]!);
  if (salt === null || expected === null) return false;
  const bits = new Uint8Array(await derive(password, salt, iterations));
  return timingSafeEqual(bits, expected);
}

/** True when the stored hash was produced with weaker parameters than current. */
export function needsRehash(encoded: string, target = PBKDF2_ITERATIONS): boolean {
  const parts = encoded.split('$');
  if (parts.length !== 4) return true;
  return Number(parts[1]) < target;
}

async function derive(password: string, salt: Uint8Array<ArrayBuffer>, iterations: number): Promise<ArrayBuffer> {
  const key = await crypto.subtle.importKey('raw', enc.encode(password), 'PBKDF2', false, ['deriveBits']);
  return crypto.subtle.deriveBits({ name: 'PBKDF2', salt, iterations, hash: 'SHA-256' }, key, 256);
}

export function timingSafeEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a[i]! ^ b[i]!;
  return diff === 0;
}

export function timingSafeEqualHex(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}

// ---------------------------------------------------------------------------
// Compact signed tokens (HS256 JWT-shaped). Used for the short-lived access
// token and for QR session tokens. Deliberately minimal — no external deps,
// no algorithm negotiation, `alg` is never read from the header.
// ---------------------------------------------------------------------------

function b64url(bytes: Uint8Array): string {
  return toBase64(bytes).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function b64urlDecode(s: string): Uint8Array<ArrayBuffer> | null {
  if (!/^[A-Za-z0-9_-]*$/.test(s)) return null;
  const pad = s.length % 4 === 0 ? '' : '='.repeat(4 - (s.length % 4));
  return tryFromBase64(s.replace(/-/g, '+').replace(/_/g, '/') + pad);
}

async function hmacKey(secret: string): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', enc.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, [
    'sign',
    'verify',
  ]);
}

export async function signToken(payload: Record<string, unknown>, secret: string): Promise<string> {
  const header = b64url(enc.encode(JSON.stringify({ alg: 'HS256', typ: 'JWT' })));
  const body = b64url(enc.encode(JSON.stringify(payload)));
  const data = `${header}.${body}`;
  const sig = await crypto.subtle.sign('HMAC', await hmacKey(secret), enc.encode(data));
  return `${data}.${b64url(new Uint8Array(sig))}`;
}

export type TokenResult<T> =
  | { readonly ok: true; readonly payload: T }
  | { readonly ok: false; readonly reason: 'MALFORMED' | 'BAD_SIGNATURE' | 'EXPIRED' | 'NOT_YET_VALID' };

export async function verifyToken<T extends { exp?: number; nbf?: number }>(
  token: string,
  secret: string,
  now: number,
): Promise<TokenResult<T>> {
  const parts = token.split('.');
  if (parts.length !== 3) return { ok: false, reason: 'MALFORMED' };
  const [header, body, sig] = parts as [string, string, string];
  const sigBytes = b64urlDecode(sig);
  if (sigBytes === null) return { ok: false, reason: 'MALFORMED' };
  const valid = await crypto.subtle.verify(
    'HMAC',
    await hmacKey(secret),
    sigBytes,
    enc.encode(`${header}.${body}`),
  );
  if (!valid) return { ok: false, reason: 'BAD_SIGNATURE' };
  const bodyBytes = b64urlDecode(body);
  if (bodyBytes === null) return { ok: false, reason: 'MALFORMED' };
  let payload: T;
  try {
    payload = JSON.parse(new TextDecoder().decode(bodyBytes)) as T;
  } catch {
    return { ok: false, reason: 'MALFORMED' };
  }
  if (typeof payload.nbf === 'number' && now < payload.nbf * 1000) {
    return { ok: false, reason: 'NOT_YET_VALID' };
  }
  if (typeof payload.exp === 'number' && now >= payload.exp * 1000) {
    return { ok: false, reason: 'EXPIRED' };
  }
  return { ok: true, payload };
}
